import type { ReactNode } from 'react';
import { useT } from '@/i18n';
import { ArrowLeft, Brand } from '@/components/ui';
import { cx } from '@/components/ui/cx';

export interface FocusShellProps {
  children: ReactNode;
  /** Back slot — omitted when there is nowhere to go back to. */
  onBack?: () => void;
  backLabel?: string;
  /** Save & exit slot — omitted for screens with nothing to save (e.g. checkout). */
  onSaveExit?: () => void;
  saveExitLabel?: string;
  /** Wider body for three-column desktop layouts (the home assessment). */
  wide?: boolean;
  /** Optional centre text in the header, e.g. "Aarav’s home assessment". */
  title?: string;
  /** Optional right-hand status (the save indicator). */
  status?: ReactNode;
}

/** Slim, single-purpose shell for assessment, checkout and generating screens. */
export const FocusShell = ({ children, onBack, backLabel, onSaveExit, saveExitLabel, wide = false, title, status }: FocusShellProps) => {
  const width = wide ? 'max-w-content' : 'max-w-narrow';
  const t = useT();

  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="skip-link">
        {t('app.skip')}
      </a>

      <header className="border-b border-line bg-white">
        <div className={cx('mx-auto flex items-center justify-between gap-3 px-4 py-3', width)}>
          <div className="flex items-center gap-2">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex min-h-tap min-w-tap items-center justify-center gap-1 rounded-md text-label text-ink-muted hover:text-lagoon-600"
              >
                <ArrowLeft className="h-5 w-5" />
                {backLabel ?? t('q.back')}
              </button>
            )}
            <Brand compact />
            {title && <span className="ml-3 hidden text-label text-ink sm:inline">{title}</span>}
          </div>

          {status && <div className="ml-auto hidden md:block">{status}</div>}

          {onSaveExit && (
            <button
              type="button"
              onClick={onSaveExit}
              className="inline-flex min-h-tap items-center rounded-md border-[1.5px] border-line px-3 text-label text-ink-soft hover:border-lagoon-500 hover:text-lagoon-600"
            >
              {saveExitLabel ?? t('q.saveExit')}
            </button>
          )}
        </div>
      </header>

      <main id="main" className={cx('mx-auto w-full flex-1 px-4 py-6 md:py-8', width)}>
        {children}
      </main>

      <footer className="border-t border-line bg-white px-4 py-4 text-center">
        <p className="text-caption text-ink-muted">{t('disclaimer.short')}</p>
      </footer>
    </div>
  );
};
